import {
	CircularProgress,
	IconButton,
	Modal,
	Stack,
	useTheme,
} from "@mui/material";
import { Share2, X } from "lucide-react";
import type { FC } from "react";
import { useHandleChatAttachmentMutation } from "web_myplann/Api/Chat/useHandleChatAttachmentMutation";
import { ChatAttachmentZoomableImage } from "web_myplann/Chat/Components/Attachments/ChatAttachmentZoomableImage";
import { ChatTopBar } from "web_myplann/Chat/Components/Conversation/ChatTopBar";
import { useHideNavbar } from "web_myplann/Shared/Hooks/useHideNavbar";
import { HandleDocumentTypes } from "web_myplann/Utils/WebViewModel";

type Props = {
	attachmentId: number;
	fileName: string;
	mimeType: string;
	src: string;
	open: boolean;
	onClose: () => void;
};

export const ChatAttachmentViewer: FC<Props> = ({
	attachmentId,
	fileName,
	mimeType,
	src,
	open,
	onClose,
}) => {
	const theme = useTheme();
	const { mutate: shareAttachment, isPending: isSharing } =
		useHandleChatAttachmentMutation();

	useHideNavbar(open);

	return (
		<Modal open={open} onClose={onClose}>
			<Stack
				tabIndex={-1}
				position="fixed"
				bgcolor={theme.palette.background.paper}
				sx={{ inset: 0, outline: "none" }}
			>
				<ChatTopBar
					icon={X}
					onIconClick={onClose}
					title={fileName}
					endAdornment={
						<IconButton
							variant="ghost"
							disabled={isSharing}
							onClick={() =>
								shareAttachment({
									attachmentId,
									fileName,
									mimeType,
									actionType: HandleDocumentTypes.SHARE,
								})
							}
						>
							{isSharing ? (
								<CircularProgress
									size={theme.iconSize.md}
									sx={{ color: theme.palette.blue[500] }}
								/>
							) : (
								<Share2
									size={theme.iconSize.md}
									color={theme.palette.grey[600]}
								/>
							)}
						</IconButton>
					}
				/>

				<Stack
					flex={1}
					minHeight={0}
					alignItems="center"
					justifyContent="center"
				>
					<ChatAttachmentZoomableImage src={src} alt={fileName} />
				</Stack>
			</Stack>
		</Modal>
	);
};
